import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { PointerEvent as ReactPointerEvent } from "react";
import { createPortal } from "react-dom";
import { Headphones, Loader2, X } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router";

import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
} from "@/components/ui/drawer";
import { C } from "@/components/modal/profile";

const TAWK_CHAT_URLS = {
  zh: "https://tawk.to/chat/6a16883e92eafb1c329837cb/1jpk08dov",
  en: "https://tawk.to/chat/6a16883e92eafb1c329837cb/1jpk0h3dp",
} as const;

const MIN_KEYBOARD_DRAWER_HEIGHT = 320;
const MAX_DRAWER_HEIGHT = 720;
const DRAWER_HEIGHT_RATIO = 0.99;
const FLOATING_BUTTON_SIZE = 58;
const FLOATING_EDGE_PADDING = 16;
const FLOATING_BOTTOM_FALLBACK_GUARD = 84;
const DRAG_OPEN_THRESHOLD = 8;
const FLOATING_Z_INDEX = 2147483000;

function buildSupportUrl(language: string, address?: string) {
  const baseUrl = language.startsWith("zh")
    ? TAWK_CHAT_URLS.zh
    : TAWK_CHAT_URLS.en;
  const url = new URL(baseUrl);

  if (address) {
    url.searchParams.set("walletAddress", address);
  }

  return url.toString();
}

function getViewportMetrics() {
  if (typeof window === "undefined") {
    return {
      keyboardInset: 0,
      drawerHeight: MAX_DRAWER_HEIGHT,
    };
  }

  const layoutHeight = window.innerHeight;
  const visualViewport = window.visualViewport;
  const visualHeight = visualViewport?.height ?? layoutHeight;
  const visualOffsetTop = visualViewport?.offsetTop ?? 0;
  const keyboardInset = Math.max(
    0,
    layoutHeight - visualHeight - visualOffsetTop
  );
  const defaultHeight = Math.min(
    layoutHeight * DRAWER_HEIGHT_RATIO,
    Math.max(MAX_DRAWER_HEIGHT, layoutHeight - 8)
  );
  const keyboardHeight = Math.max(
    MIN_KEYBOARD_DRAWER_HEIGHT,
    Math.min(defaultHeight, visualHeight - 8)
  );

  return {
    keyboardInset,
    drawerHeight: keyboardInset > 0 ? keyboardHeight : defaultHeight,
  };
}

interface CustomerSupportEntryProps {
  walletAddress?: string;
}

interface FloatingPosition {
  x: number;
  y: number;
}

function getInitialFloatingPosition(): FloatingPosition {
  if (typeof window === "undefined") {
    return { x: FLOATING_EDGE_PADDING, y: FLOATING_EDGE_PADDING };
  }

  const bounds = getFloatingBounds();

  return {
    x: bounds.maxX,
    y: bounds.maxY,
  };
}

function getBottomBlockedHeight() {
  if (typeof document === "undefined") return FLOATING_BOTTOM_FALLBACK_GUARD;

  const bottomNav = document.querySelector("nav.fixed.bottom-0");
  const navHeight = bottomNav?.getBoundingClientRect().height ?? 0;

  return Math.max(navHeight, FLOATING_BOTTOM_FALLBACK_GUARD);
}

function getFloatingBounds() {
  if (typeof window === "undefined") {
    return {
      minX: FLOATING_EDGE_PADDING,
      maxX: FLOATING_EDGE_PADDING,
      minY: FLOATING_EDGE_PADDING,
      maxY: FLOATING_EDGE_PADDING,
    };
  }

  const maxX = window.innerWidth - FLOATING_BUTTON_SIZE - FLOATING_EDGE_PADDING;
  const maxY =
    window.innerHeight -
    getBottomBlockedHeight() -
    FLOATING_BUTTON_SIZE -
    FLOATING_EDGE_PADDING;

  return {
    minX: FLOATING_EDGE_PADDING,
    maxX: Math.max(FLOATING_EDGE_PADDING, maxX),
    minY: FLOATING_EDGE_PADDING,
    maxY: Math.max(FLOATING_EDGE_PADDING, maxY),
  };
}

function clampFloatingPosition(position: FloatingPosition): FloatingPosition {
  if (typeof window === "undefined") return position;
  const bounds = getFloatingBounds();

  return {
    x: Math.min(Math.max(position.x, bounds.minX), bounds.maxX),
    y: Math.min(Math.max(position.y, bounds.minY), bounds.maxY),
  };
}

export function CustomerSupportEntry({
  walletAddress,
}: CustomerSupportEntryProps) {
  const location = useLocation();
  const { i18n, t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [iframeLoaded, setIframeLoaded] = useState(false);
  const [viewportMetrics, setViewportMetrics] = useState(getViewportMetrics);
  const [portalReady, setPortalReady] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [floatingPosition, setFloatingPosition] = useState(
    getInitialFloatingPosition
  );
  const dragStateRef = useRef<{
    pointerId: number;
    startX: number;
    startY: number;
    originX: number;
    originY: number;
    moved: boolean;
  } | null>(null);
  const dragCleanupRef = useRef<(() => void) | null>(null);
  const keyboardOpen = viewportMetrics.keyboardInset > 0;
  const supportUrl = useMemo(
    () => buildSupportUrl(i18n.language, walletAddress),
    [i18n.language, walletAddress]
  );
  const shouldShow = location.pathname === "/";

  useEffect(() => {
    setPortalReady(true);
  }, []);

  useEffect(() => {
    if (shouldShow) return;
    setOpen(false);
    dragCleanupRef.current?.();
    dragCleanupRef.current = null;
    dragStateRef.current = null;
    setIsDragging(false);
  }, [shouldShow]);

  useEffect(() => {
    return () => {
      dragCleanupRef.current?.();
    };
  }, []);

  useEffect(() => {
    if (!open || typeof window === "undefined") return;

    const updateViewportMetrics = () =>
      setViewportMetrics(getViewportMetrics());
    const visualViewport = window.visualViewport;

    updateViewportMetrics();
    window.addEventListener("resize", updateViewportMetrics);
    visualViewport?.addEventListener("resize", updateViewportMetrics);
    visualViewport?.addEventListener("scroll", updateViewportMetrics);

    return () => {
      window.removeEventListener("resize", updateViewportMetrics);
      visualViewport?.removeEventListener("resize", updateViewportMetrics);
      visualViewport?.removeEventListener("scroll", updateViewportMetrics);
    };
  }, [open]);

  useEffect(() => {
    if (!open || typeof window === "undefined") return;

    const scrollY = window.scrollY;
    const bodyStyle = document.body.style;
    const htmlStyle = document.documentElement.style;
    const previousBodyStyles = {
      position: bodyStyle.position,
      top: bodyStyle.top,
      left: bodyStyle.left,
      right: bodyStyle.right,
      width: bodyStyle.width,
      overflow: bodyStyle.overflow,
    };
    const previousHtmlStyles = {
      overflow: htmlStyle.overflow,
      overscrollBehavior: htmlStyle.overscrollBehavior,
    };

    bodyStyle.position = "fixed";
    bodyStyle.top = `-${scrollY}px`;
    bodyStyle.left = "0";
    bodyStyle.right = "0";
    bodyStyle.width = "100%";
    bodyStyle.overflow = "hidden";
    htmlStyle.overflow = "hidden";
    htmlStyle.overscrollBehavior = "none";

    return () => {
      bodyStyle.position = previousBodyStyles.position;
      bodyStyle.top = previousBodyStyles.top;
      bodyStyle.left = previousBodyStyles.left;
      bodyStyle.right = previousBodyStyles.right;
      bodyStyle.width = previousBodyStyles.width;
      bodyStyle.overflow = previousBodyStyles.overflow;
      htmlStyle.overflow = previousHtmlStyles.overflow;
      htmlStyle.overscrollBehavior = previousHtmlStyles.overscrollBehavior;
      window.scrollTo(0, scrollY);
    };
  }, [open]);

  useEffect(() => {
    if (typeof window === "undefined") return;

    const keepInViewport = () =>
      setFloatingPosition(position => clampFloatingPosition(position));

    window.addEventListener("resize", keepInViewport);
    return () => window.removeEventListener("resize", keepInViewport);
  }, []);

  const openSupport = useCallback(() => {
    setIframeLoaded(false);
    setOpen(true);
  }, []);

  const handleFloatingPointerDown = useCallback(
    (event: ReactPointerEvent<HTMLButtonElement>) => {
      event.preventDefault();
      dragCleanupRef.current?.();
      try {
        event.currentTarget.setPointerCapture(event.pointerId);
      } catch {
        // Some embedded mobile browsers expose PointerEvent without capture.
      }
      setIsDragging(true);
      dragStateRef.current = {
        pointerId: event.pointerId,
        startX: event.clientX,
        startY: event.clientY,
        originX: floatingPosition.x,
        originY: floatingPosition.y,
        moved: false,
      };

      const handleWindowPointerMove = (moveEvent: PointerEvent) => {
        const dragState = dragStateRef.current;
        if (!dragState || dragState.pointerId !== moveEvent.pointerId) return;
        moveEvent.preventDefault();

        const deltaX = moveEvent.clientX - dragState.startX;
        const deltaY = moveEvent.clientY - dragState.startY;
        const distance = Math.hypot(deltaX, deltaY);

        if (distance > DRAG_OPEN_THRESHOLD) {
          dragState.moved = true;
        }

        setFloatingPosition(
          clampFloatingPosition({
            x: dragState.originX + deltaX,
            y: dragState.originY + deltaY,
          })
        );
      };

      const cleanupDragListeners = () => {
        window.removeEventListener("pointermove", handleWindowPointerMove);
        window.removeEventListener("pointerup", handleWindowPointerUp);
        window.removeEventListener("pointercancel", handleWindowPointerCancel);
      };

      const finishDrag = (upEvent: PointerEvent) => {
        const dragState = dragStateRef.current;
        if (!dragState || dragState.pointerId !== upEvent.pointerId) return;

        upEvent.preventDefault();
        const moved = dragState.moved;
        dragStateRef.current = null;
        dragCleanupRef.current = null;
        setIsDragging(false);
        cleanupDragListeners();

        if (!moved) {
          openSupport();
          return;
        }

        setFloatingPosition(position => {
          const clamped = clampFloatingPosition(position);
          const bounds = getFloatingBounds();
          const shouldSnapLeft =
            clamped.x + FLOATING_BUTTON_SIZE / 2 < window.innerWidth / 2;

          return {
            x: shouldSnapLeft ? bounds.minX : bounds.maxX,
            y: clamped.y,
          };
        });
      };

      function handleWindowPointerUp(upEvent: PointerEvent) {
        finishDrag(upEvent);
      }

      function handleWindowPointerCancel(cancelEvent: PointerEvent) {
        if (dragStateRef.current?.pointerId !== cancelEvent.pointerId) return;
        dragStateRef.current = null;
        dragCleanupRef.current = null;
        setIsDragging(false);
        cleanupDragListeners();
        setFloatingPosition(position => clampFloatingPosition(position));
      }

      window.addEventListener("pointermove", handleWindowPointerMove, {
        passive: false,
      });
      window.addEventListener("pointerup", handleWindowPointerUp, {
        passive: false,
      });
      window.addEventListener("pointercancel", handleWindowPointerCancel, {
        passive: false,
      });
      dragCleanupRef.current = cleanupDragListeners;
    },
    [floatingPosition.x, floatingPosition.y, openSupport]
  );

  if (!portalReady || !shouldShow) return null;

  return createPortal(
    <>
      {!open && (
        <button
          type="button"
          onPointerDown={handleFloatingPointerDown}
          onPointerCancel={() => {
            dragCleanupRef.current?.();
            dragCleanupRef.current = null;
            dragStateRef.current = null;
            setIsDragging(false);
            setFloatingPosition(position => clampFloatingPosition(position));
          }}
          className="fixed rounded-2xl flex items-center justify-center active:scale-95 touch-none select-none"
          style={{
            left: floatingPosition.x,
            top: floatingPosition.y,
            width: FLOATING_BUTTON_SIZE,
            height: FLOATING_BUTTON_SIZE,
            zIndex: FLOATING_Z_INDEX,
            transition: isDragging
              ? "none"
              : "left 180ms ease, top 180ms ease, box-shadow 180ms ease, transform 120ms ease",
            background:
              "linear-gradient(145deg, rgba(7,29,20,0.96), rgba(11,47,31,0.96))",
            border: "1px solid rgba(34,197,94,0.35)",
            boxShadow:
              "0 10px 28px rgba(0,0,0,0.35), 0 0 24px rgba(34,197,94,0.18)",
            color: C.primary,
          }}
          aria-label={t("profilePage.customerSupport.title")}
        >
          <Headphones size={25} strokeWidth={2.2} />
        </button>
      )}

      <Drawer
        open={open}
        onOpenChange={setOpen}
        direction="bottom"
        modal
        disablePreventScroll={false}
        repositionInputs
      >
        <DrawerContent
          className="z-[2147483000] mx-auto w-full max-w-lg overflow-hidden rounded-t-2xl"
          style={{
            background: C.card,
            borderColor: C.border,
            bottom: viewportMetrics.keyboardInset,
            height: viewportMetrics.drawerHeight,
            maxHeight: viewportMetrics.drawerHeight,
          }}
          onPointerDown={event => event.stopPropagation()}
          onTouchMove={event => event.stopPropagation()}
        >
          {!keyboardOpen && (
            <DrawerHeader
              className="px-4 pt-2 pb-2 text-left"
              style={{ borderBottom: `1px solid ${C.border}` }}
            >
              <div className="flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <DrawerTitle
                    className="text-sm font-semibold"
                    style={{ color: C.text }}
                  >
                    {t("profilePage.customerSupport.title")}
                  </DrawerTitle>
                </div>
                <button
                  type="button"
                  onClick={() => setOpen(false)}
                  className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0 transition-all hover:opacity-80 active:scale-95"
                  style={{
                    background: C.card2,
                    border: `1px solid ${C.border}`,
                    color: C.muted,
                  }}
                  aria-label={t("profilePage.customerSupport.close")}
                >
                  <X size={16} />
                </button>
              </div>
            </DrawerHeader>
          )}

          <div
            className="relative flex-1 min-h-0 overscroll-contain"
            style={{ background: C.bg }}
            onTouchMove={event => event.stopPropagation()}
          >
            {!iframeLoaded && (
              <div
                className="absolute inset-0 flex items-center justify-center gap-2 text-xs"
                style={{ color: C.dim }}
              >
                <Loader2 size={16} className="animate-spin" />
                {t("profilePage.customerSupport.loading")}
              </div>
            )}
            {open && (
              <iframe
                title={t("profilePage.customerSupport.iframeTitle")}
                src={supportUrl}
                className="w-full h-full border-0"
                allow="clipboard-write; microphone"
                referrerPolicy="strict-origin-when-cross-origin"
                onLoad={() => setIframeLoaded(true)}
              />
            )}
          </div>
        </DrawerContent>
      </Drawer>
    </>,
    document.body
  );
}
